import { GridComponent } from '@syncfusion/ej2-react-grids';

const FILTER_MENU_UID_PREFIX = 'e-flmenu-';

/**
 * Syncfusion renders each header's Excel filter trigger as an aria-hidden, role-less div whose only
 * "filtered" signal is the e-filtered class. Give e2e tests a stable handle on it: data-testid
 * `filter-menu-{field}`, plus data-filtered mirroring Syncfusion's own e-filtered state, so a test fails
 * exactly when Syncfusion stops painting the filtered icon (see the bulk edit cancel regression).
 */
export const syncFilterMenuTestAttributes = (grid: GridComponent | null | undefined): void => {
    const gridElement = grid?.element;
    if (!grid || !gridElement) {
        return;
    }
    gridElement.querySelectorAll<HTMLElement>('.e-filtermenudiv').forEach((icon) => {
        const uid = icon.getAttribute('e-mappinguid')?.replace(FILTER_MENU_UID_PREFIX, '');
        const field = uid ? grid.getColumnByUid(uid)?.field : undefined;
        if (!field) {
            return;
        }
        const testId = `filter-menu-${field}`;
        if (icon.getAttribute('data-testid') !== testId) {
            icon.setAttribute('data-testid', testId);
        }
        const filtered = String(icon.classList.contains('e-filtered'));
        if (icon.getAttribute('data-filtered') !== filtered) {
            icon.setAttribute('data-filtered', filtered);
        }
    });
};

/**
 * Keeps the attributes above in sync while the grid lives. Observes only the header (class flips on the
 * filter icons, and header re-renders such as Column Chooser changes), never the virtualized content rows.
 * Returns a cleanup function.
 */
export const observeFilterMenuTestAttributes = (grid: GridComponent | null | undefined): (() => void) => {
    const header = grid?.element?.querySelector('.e-gridheader');
    if (!header) {
        return () => undefined;
    }
    syncFilterMenuTestAttributes(grid);
    const observer = new MutationObserver(() => syncFilterMenuTestAttributes(grid));
    observer.observe(header, { subtree: true, childList: true, attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
};
